import { useVersionStore } from "@/stores/versionStore";

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

interface PingResponse {
  version: string;
  status: string;
  timestamp: string;
}

interface VersionCheckCallbacks {
  showInfo: (message: string, options?: { autoHideDuration?: number | null; anchorOrigin?: { vertical: "top" | "bottom"; horizontal: "left" | "center" | "right" }; onClick?: () => void }) => string;
  removeMessages: (ids: string[]) => void;
}

let initialVersion: string | null = null;
let snackbarId: string | null = null;
let intervalId: ReturnType<typeof setInterval> | null = null;
let callbacks: VersionCheckCallbacks | null = null;

const fetchVersion = async (): Promise<string | null> => {
  try {
    const response = await fetch("/api/ping");
    if (!response.ok) return null;
    const data: PingResponse = await response.json();
    return data.version || null;
  } catch {
    return null;
  }
};

const checkVersion = async (): Promise<void> => {
  if (document.visibilityState !== "visible") return;
  if (!callbacks) return;

  const currentVersion = await fetchVersion();
  if (!currentVersion) return;

  if (initialVersion === null) {
    initialVersion = currentVersion;
    return;
  }

  if (currentVersion !== initialVersion) {
    useVersionStore.getState().setUpdateAvailable(true);

    if (snackbarId) {
      callbacks.removeMessages([snackbarId]);
    }
    snackbarId = callbacks.showInfo("A new version is available. Click here to refresh.", {
      autoHideDuration: null,
      anchorOrigin: { vertical: "top", horizontal: "center" },
      onClick: () => window.location.reload(),
    });
  }
};

const handleVisibilityChange = (): void => {
  if (document.visibilityState === "visible") {
    checkVersion();
  }
};

export const initializeVersionCheck = (versionCallbacks: VersionCheckCallbacks): (() => void) => {
  callbacks = versionCallbacks;

  checkVersion();

  intervalId = setInterval(checkVersion, POLL_INTERVAL_MS);
  document.addEventListener("visibilitychange", handleVisibilityChange);

  return () => {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    callbacks = null;
    initialVersion = null;
    snackbarId = null;
  };
};
